/**
 * Anonymous client error reporting (same origin, no personal data).
 *
 * Catches uncaught errors and unhandled promise rejections, extracts the error
 * message and stack trace (every URL scrubbed by `scrubUrls`), and the high-level
 * route pattern (e.g. `/round/:id` — never the raw identifier), and posts a
 * compact JSON payload to `/api/client-errors`.
 *
 * Payload shape: exactly `{ v: 1, build, route, kind, message, stack, count }`.
 * No URL queries, no user agents, no cookies, no user identifiers.
 */

import { scrubUrls } from "../../functions/_lib/scrub-urls.js";
import { BUILD_SHA, CLIENT_ERRORS, SITE_ORIGIN } from "../config.js";
import { matchPattern } from "../router.js";

export interface ClientErrorPayload {
  v: 1;
  build: string;
  route: string;
  kind: "error" | "rejection";
  message: string;
  stack: string;
  count: number;
}

export interface ReporterOptions {
  send?: (payload: ClientErrorPayload) => void | Promise<void>;
  now?: () => number;
  maxPerPage?: number;
  build?: string;
  getRoute?: () => string;
  enabled?: boolean;
}

export interface Reporter {
  (kind: "error" | "rejection", error: unknown): boolean;
  report(kind: "error" | "rejection", error: unknown): boolean;
}

/**
 * Pure route normalizer: derives the route pattern (e.g. `/round/:id`)
 * from the path by matching against the router's own route table.
 *
 * Strips queries and hashes first. Returns `"(unknown)"` if the path
 * does not match any registered route, ensuring raw path parameters or
 * unexpected URLs never leak into error logs.
 */
export function normalizeRoute(path?: string): string {
  const p = path ?? (typeof location !== "undefined" ? location.pathname : "/");
  const match = matchPattern(p);
  return match ?? "(unknown)";
}

/** Pure string truncation to at most `n` characters. */
export function truncate(text: string, n: number): string {
  if (typeof text !== "string") return "";
  return text.length <= n ? text : text.slice(0, n);
}

/** Origins whose URLs keep their path in a report: the site, plus wherever this bundle is served (preview, dev). */
function siteOrigins(): string[] {
  return typeof location !== "undefined" && location.origin ? [SITE_ORIGIN, location.origin] : [SITE_ORIGIN];
}

/** Find the first stack frame line in a stack trace. */
function firstStackFrame(stack: string): string {
  if (typeof stack !== "string") return "";
  const lines = stack.split("\n").map((l) => l.trim()).filter(Boolean);
  const frame = lines.find((l) => /^at\s+/i.test(l) || l.includes("@"));
  return frame ?? lines[0] ?? "";
}

/** Deduplication key: message + first stack frame. */
export function reportKey(message: string, stack: string): string {
  const frame = firstStackFrame(stack);
  return `${(message ?? "").trim()}::${frame.trim()}`;
}

/** Extract message and stack from an arbitrary error or rejection reason. */
function extractErrorDetails(error: unknown): { message: string; stack: string } {
  if (error instanceof Error) {
    return {
      message: error.message || error.name || "Error",
      stack: error.stack || "",
    };
  }

  if (typeof error === "object" && error !== null) {
    const errObj = error as { error?: unknown; reason?: unknown; message?: unknown; stack?: unknown };
    if (errObj.error instanceof Error) {
      return extractErrorDetails(errObj.error);
    }
    if (errObj.reason instanceof Error) {
      return extractErrorDetails(errObj.reason);
    }
    const message = typeof errObj.message === "string" ? errObj.message : typeof errObj.reason === "string" ? errObj.reason : String(error);
    const stack = typeof errObj.stack === "string" ? errObj.stack : "";
    return { message, stack };
  }

  return {
    message: typeof error === "string" ? error : String(error ?? "Unknown error"),
    stack: "",
  };
}

/**
 * Create an error reporter with deduplication and a per-page-load cap.
 *
 * Pure factory: takes `send`, `now`, and `maxPerPage` (default 5).
 * Never throws.
 */
export function createReporter(options: ReporterOptions = {}): Reporter {
  const maxPerPage = options.maxPerPage ?? 5;
  const enabled = options.enabled ?? true;
  const seen = new Set<string>();
  let sentCount = 0;

  const reportFn = (kind: "error" | "rejection", error: unknown): boolean => {
    try {
      if (!enabled) return false;
      if (sentCount >= maxPerPage) return false;

      const details = extractErrorDetails(error);
      const origins = siteOrigins();
      const rawMessage = scrubUrls(details.message, origins);
      const cleanStack = scrubUrls(details.stack, origins);

      const key = reportKey(rawMessage, cleanStack);
      if (seen.has(key)) return false;
      seen.add(key);

      const message = truncate(rawMessage, 300);
      const stack = truncate(cleanStack, 2000);
      const route = truncate(
        normalizeRoute(options.getRoute ? options.getRoute() : (typeof location !== "undefined" ? location.pathname : "/")),
        100,
      );
      const build = truncate(options.build ?? BUILD_SHA, 64);

      const payload: ClientErrorPayload = {
        v: 1,
        build,
        route,
        kind,
        message,
        stack,
        count: 1,
      };

      sentCount++;

      if (options.send) {
        const sendResult = options.send(payload);
        if (sendResult && typeof (sendResult as Promise<void>).catch === "function") {
          (sendResult as Promise<void>).catch(() => {});
        }
      }
      return true;
    } catch {
      return false;
    }
  };

  reportFn.report = reportFn;
  return reportFn;
}

/**
 * Default transport: tries `navigator.sendBeacon` with a JSON Blob, falling
 * back to `fetch('/api/client-errors', { method: 'POST', keepalive: true })`.
 * Never throws.
 */
function defaultSend(payload: ClientErrorPayload): void {
  try {
    const body = JSON.stringify(payload);
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function" && typeof Blob === "function") {
      try {
        const blob = new Blob([body], { type: "application/json" });
        if (navigator.sendBeacon("/api/client-errors", blob)) {
          return;
        }
      } catch {
        // Fall through to fetch
      }
    }

    if (typeof fetch === "function") {
      fetch("/api/client-errors", {
        method: "POST",
        keepalive: true,
        headers: { "Content-Type": "application/json" },
        body,
      }).catch(() => {});
    }
  } catch {
    // Never throw
  }
}

let installed = false;

/**
 * Install global error and unhandled rejection listeners on `window`.
 *
 * No-op if `CLIENT_ERRORS` is false or `window` is absent. Never throws.
 */
export function installClientErrorReporter(options?: ReporterOptions): (() => void) | undefined {
  try {
    const enabled = options?.enabled ?? CLIENT_ERRORS;
    if (!enabled) return undefined;
    if (typeof window === "undefined") return undefined;
    if (installed) return undefined;
    installed = true;

    const reporter = createReporter({
      send: defaultSend,
      ...options,
      enabled: true,
    });

    const onError = (event: ErrorEvent): void => {
      try {
        // A cross-origin script's error is masked to exactly this, with no error
        // object: nothing actionable, and mostly extensions and third parties.
        if (event.message === "Script error." && event.error == null) return;
        reporter.report("error", event.error ?? event.message);
      } catch {
        // Never throw
      }
    };

    const onRejection = (event: PromiseRejectionEvent): void => {
      try {
        reporter.report("rejection", event.reason);
      } catch {
        // Never throw
      }
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      installed = false;
    };
  } catch {
    return undefined;
  }
}
